import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoRefreshToken,
} from "amazon-cognito-identity-js";
import useLocalStorage from "../hooks/useLocalStorage";
import {
  registerAuthRefresher,
  unregisterAuthRefresher,
} from "../api/authFetch";

// Create auth context
const AuthContext = createContext(null);

/**
 * Custom hook to use the auth context
 * @returns {Object} Auth context value
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

/**
 * Auth provider component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
export const AuthProvider = ({ children }) => {
  // Persist auth state in localStorage
  const [token, setToken, removeToken] = useLocalStorage("auth_token", null);
  const [refreshToken, setRefreshToken, removeRefreshToken] = useLocalStorage(
    "refresh_token",
    null
  );
  const [userInfo, setUserInfo, removeUserInfo] = useLocalStorage(
    "user_info",
    null
  );
  const [userRole, setUserRole, removeUserRole] = useLocalStorage(
    "user_role",
    null
  );

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Ref to track if we're already refreshing
  const isRefreshing = useRef(false);

  // Check if user is authenticated
  const isAuthenticated = Boolean(token && userInfo);

  // Get Cognito configuration
  const getCognitoConfig = useCallback(() => {
    return {
      userPoolId: window.COGNITO_CONFIG?.userPoolId || "us-east-1_fc1omeTGh",
      clientId: window.COGNITO_CONFIG?.clientId || "54fiucjkk95o6gna3l5l3dnjuo",
      region: window.COGNITO_CONFIG?.region || "us-east-1",
    };
  }, []);

  // Create Cognito User Pool
  const getUserPool = useCallback(() => {
    const config = getCognitoConfig();
    return new CognitoUserPool({
      UserPoolId: config.userPoolId,
      ClientId: config.clientId,
    });
  }, [getCognitoConfig]);

  /**
   * Login user with email and password using Cognito
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<Object>} Login result
   */
  const login = useCallback(
    async (email, password) => {
      setLoading(true);
      setError(null);

      try {
        // Create Cognito authentication details
        const authenticationDetails = new AuthenticationDetails({
          Username: email,
          Password: password,
        });

        // Get user pool and create user
        const userPool = getUserPool();
        const cognitoUser = new CognitoUser({
          Username: email,
          Pool: userPool,
        });

        // Return a promise for the authentication
        return new Promise((resolve, reject) => {
          cognitoUser.authenticateUser(authenticationDetails, {
            onSuccess: (result) => {
              // Get the ID token and refresh token
              const idToken = result.getIdToken().getJwtToken();
              const refreshTokenValue = result.getRefreshToken().getToken();

              // Store tokens in context and localStorage
              setToken(idToken);
              setRefreshToken(refreshTokenValue);

              // Parse user info from token
              try {
                const payload = JSON.parse(atob(idToken.split(".")[1]));
                const userInfoData = {
                  email: payload.email,
                  sub: payload.sub,
                  groups: payload["cognito:groups"] || [],
                };

                setUserInfo(userInfoData);
                setUserRole(
                  payload["cognito:groups"]?.includes("Admins")
                    ? "admin"
                    : payload["cognito:groups"]?.includes("Captains")
                    ? "captain"
                    : payload["cognito:groups"]?.includes("Members")
                    ? "member"
                    : "user"
                );

                // Store in session storage for backward compatibility
                sessionStorage.setItem("cognitoIdToken", idToken);
                sessionStorage.setItem("cognitoRefreshToken", refreshTokenValue);
              } catch (e) {
                console.error("Error parsing JWT token:", e);
              }

              setLoading(false);
              resolve({ success: true, data: { token: idToken } });
            },
            onFailure: (err) => {
              console.error("Authentication error:", err);
              setError(err.message || "Authentication failed");
              setLoading(false);
              resolve({ success: false, error: err.message });
            },
            newPasswordRequired: (userAttributes, requiredAttributes) => {
              setError(
                "You need to change your password. Please contact an administrator."
              );
              setLoading(false);
              resolve({
                success: false,
                error: "New password required",
                newPasswordRequired: true,
              });
            },
          });
        });
      } catch (err) {
        setError(err.message || "Login failed");
        setLoading(false);
        return { success: false, error: err.message };
      }
    },
    [getUserPool, setToken, setRefreshToken, setUserInfo, setUserRole]
  );

  /**
   * Logout user
   */
  const logout = useCallback(() => {
    // Clear auth state
    removeToken();
    removeRefreshToken();
    removeUserInfo();
    removeUserRole();

    // Clear session storage
    sessionStorage.removeItem("cognitoIdToken");
    sessionStorage.removeItem("cognitoRefreshToken");
    sessionStorage.removeItem("userProfileInfo");

    // Sign out of Cognito
    const userPool = getUserPool();
    const currentUser = userPool.getCurrentUser();
    if (currentUser) {
      currentUser.signOut();
    }
  }, [getUserPool, removeToken, removeRefreshToken, removeUserInfo, removeUserRole]);

  /**
   * Refresh the session using the refresh token
   * @returns {Promise<boolean>} Success status
   */
  const refreshSession = useCallback(async () => {
    if (!refreshToken || isRefreshing.current) {
      return false;
    }

    isRefreshing.current = true;
    
    try {
      const userPool = getUserPool();
      const cognitoRefreshToken = new CognitoRefreshToken({
        RefreshToken: refreshToken,
      });

      // Get current user. In a fresh browser session the SDK's own
      // storage can be gone while our persisted auth remains —
      // rebuild the user handle from the stored email so the refresh
      // still works (#250).
      let currentUser = userPool.getCurrentUser();
      if (!currentUser && userInfo && userInfo.email) {
        currentUser = new CognitoUser({
          Username: userInfo.email,
          Pool: userPool,
        });
      }
      if (!currentUser) {
        isRefreshing.current = false;
        return false;
      }

      return new Promise((resolve) => {
        currentUser.refreshSession(cognitoRefreshToken, (err, session) => {
          if (err) {
            console.error("Error refreshing session:", err);
            isRefreshing.current = false;
            resolve(false);
            return;
          }

          // Update tokens
          const newIdToken = session.getIdToken().getJwtToken();
          const newRefreshToken = session.getRefreshToken().getToken();
          
          setToken(newIdToken);
          setRefreshToken(newRefreshToken);

          // Update session storage
          sessionStorage.setItem("cognitoIdToken", newIdToken);
          sessionStorage.setItem("cognitoRefreshToken", newRefreshToken);

          console.log("Session refreshed successfully");
          isRefreshing.current = false;
          resolve(true);
        });
      });
    } catch (error) {
      console.error("Error during refresh:", error);
      isRefreshing.current = false;
      return false;
    }
  }, [refreshToken, getUserPool, setToken, setRefreshToken, userInfo]);

  /**
   * Get auth headers for API requests
   * @returns {Object} Headers with Authorization token
   */
  const getAuthHeaders = useCallback(() => {
    if (!token) return {};
    return {
      Authorization: `Bearer ${token}`,
    };
  }, [token]);

  // Check for existing token on mount
  useEffect(() => {
    // Check session storage for existing token
    const storedToken = sessionStorage.getItem("cognitoIdToken");
    const storedRefreshToken = sessionStorage.getItem("cognitoRefreshToken");

    // Reverse sync (#250): our auth persists in localStorage, but the
    // bare API clients (graphql client, authFetch) read
    // sessionStorage.cognitoIdToken — which is empty in a NEW browser
    // session. Without this backfill the UI renders logged-in while
    // every API call goes out unauthenticated.
    if (!storedToken && token) {
      sessionStorage.setItem("cognitoIdToken", token);
      if (refreshToken) {
        sessionStorage.setItem("cognitoRefreshToken", refreshToken);
      }
    }

    if (storedToken && !token) {
      // If found in session storage but not in our context, restore it
      setToken(storedToken);
      
      if (storedRefreshToken) {
        setRefreshToken(storedRefreshToken);
      }

      // Parse token to get user info
      try {
        const payload = JSON.parse(atob(storedToken.split(".")[1]));

        const userInfoData = {
          email: payload.email,
          sub: payload.sub,
          groups: payload["cognito:groups"] || [],
        };

        setUserInfo(userInfoData);
        setUserRole(
          payload["cognito:groups"]?.includes("Admins")
            ? "admin"
            : payload["cognito:groups"]?.includes("Captains")
            ? "captain"
            : payload["cognito:groups"]?.includes("Members")
            ? "member"
            : "user"
        );
      } catch (e) {
        console.error("Error parsing stored JWT token:", e);
      }
    }
  }, [token, refreshToken, setToken, setRefreshToken, setUserInfo, setUserRole]);

  // Automatically refresh token before it expires
  useEffect(() => {
    if (!token || !refreshToken) return;

    try {
      // Parse token to check expiration
      const payload = JSON.parse(atob(token.split(".")[1]));
      const expirationTime = payload.exp * 1000; // Convert to milliseconds
      const currentTime = Date.now();
      const timeUntilExpiry = expirationTime - currentTime;

      // Refresh 5 minutes before expiry
      const refreshTime = timeUntilExpiry - (5 * 60 * 1000);

      if (refreshTime > 0) {
        const timer = setTimeout(() => {
          console.log("Token expiring soon, refreshing...");
          refreshSession();
        }, refreshTime);

        return () => clearTimeout(timer);
      } else {
        // Expired, or expiring within 5 minutes — the classic "came
        // back later" case (#250). The old code did NOTHING when the
        // token was already expired, leaving the UI half-logged-in
        // while every API call failed. Refresh right away; if the
        // refresh token is dead too, log out cleanly so the pages
        // render a real logged-out state instead of jank.
        const alreadyExpired = timeUntilExpiry <= 0;
        console.log(
          alreadyExpired
            ? "Token expired, refreshing session..."
            : "Token expires soon, refreshing immediately..."
        );
        refreshSession().then((ok) => {
          if (!ok && alreadyExpired) {
            console.log("Refresh failed with expired token — logging out");
            logout();
          }
        });
      }
    } catch (e) {
      console.error("Error checking token expiration:", e);
    }
  }, [token, refreshToken, refreshSession, logout]);

  // Register refreshSession with the plain-module authFetch wrapper so that
  // bare API clients (web/src/api/*) can transparently refresh on 401.
  // refreshSession is wrapped in useCallback above, so this only re-registers
  // when its dependencies actually change.
  useEffect(() => {
    registerAuthRefresher(refreshSession);
    return () => {
      unregisterAuthRefresher();
    };
  }, [refreshSession]);

  // Auth context value
  const value = {
    token,
    userInfo,
    userRole,
    isAuthenticated,
    loading,
    error,
    login,
    logout,
    getAuthHeaders,
    refreshSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
